// @flow

import glamorous from 'glamorous';
import pluralize from 'pluralize';
import { get } from 'lodash/fp';
import { connect } from 'react-redux';
import React, { Component } from 'react';
import * as challengeActions from 'app/actions/challengeActions';
import { isPlayerWinning, isPlayerLosing } from 'app/utils/rapidfireUtils';
import {
  LIVE_CHALLENGE_POLL_DURATION,
  OPEN_CHALLENGE_POLL_DURATION
} from '../../constants/pollDurations';
import matchUpsSelector from '../../selectors/matchUpsSelector';
import LivePlayer from './LivePlayer';
import MatchUps from './MatchUps';

type Props = {
  matchUps: Array<Array<Object>>,
  numDraws: number,
  numWinning: number,
  prize?: number,
  rapidfireType: string,
  sportId: number,
  winningAlgorithm: string,
  setPollDuration: (duration: number) => void,
  startPoll: () => void,
  stopPoll: () => void
};

const Results = glamorous.div(({ theme, isWinning }) => ({
  backgroundColor: isWinning ? theme.color.success : theme.color.basis3,
  width: '100%',
  color: theme.color.white,
  textAlign: 'center',
  padding: theme.spacing.xsmall,
  transition: 'all 0.2s',
  [theme.mediaQuery.desktopAndUp]: {
    padding: theme.spacing.small
  }
}));

const Prize = glamorous.span(({ theme }) => ({
  padding: theme.spacing.small,
  fontWeight: theme.fontWeight.demiBold,
  fontSize: theme.fontSize.large
}));

const Trophy = glamorous.span(
  {
    paddingRight: '0.2em'
  },
  'ion-trophy'
);

class RapidFireLiveChallenge extends Component<Props> {
  props: Props;
  static defaultProps = { prize: false };

  componentDidMount() {
    this.props.setPollDuration(LIVE_CHALLENGE_POLL_DURATION);
    this.props.startPoll();
  }

  componentWillUnmount() {
    this.props.setPollDuration(OPEN_CHALLENGE_POLL_DURATION);
    this.props.stopPoll();
  }

  render() {
    const {
      matchUps,
      rapidfireType,
      numWinning,
      numDraws,
      prize,
      sportId,
      winningAlgorithm
    } = this.props;
    const numToWin = rapidfireType === 'rapidfire_3' ? 3 : 5;
    const isWinning = prize;

    return (
      <div>
        <Results isWinning={isWinning}>
          <span>{`${numWinning}/${numToWin} WON`}</span>
          {numDraws !== undefined && <span>{` (${numDraws} ${pluralize('draw', numDraws)})`}</span>}
          {isWinning && (
            <Prize>
              <Trophy />${prize}
            </Prize>
          )}
        </Results>
        <MatchUps
          useVsBackground
          matchUps={matchUps}
          renderPlayer={(player, opponent, index, isYou) => (
            <LivePlayer
              id={player.id}
              name={player.name}
              progress={player.progress}
              awayAbbr={player.awayAbbr}
              homeAbbr={player.homeAbbr}
              image={player.imageURI}
              isHome={player.isHome}
              isTeam={player.isTeam}
              reverseOrder={player.reverseOrder}
              initials={player.initials}
              isWinning={!player.ruledOut && isPlayerWinning(player, opponent, winningAlgorithm)}
              isLosing={!player.ruledOut && isPlayerLosing(player, opponent, winningAlgorithm)}
              isLive
              labels={player.labels.filter(label => label.id === 'O')}
              score={player.score}
              isYou={isYou}
              reverseLayout={!isYou}
              hideMatchUpLine={sportId === 99}
            />
          )}
        />
      </div>
    );
  }
}

function mapStateToProps(state) {
  const score = get('entry.myEntry.score', state);
  const [numWinning, numDraws] = score ? score.toString().split('.') : [0, undefined];
  const payStructure = state.rapidfire.payStructures.find(
    p => p.key === state.challenge.rapidfireType
  );

  return {
    matchUps: matchUpsSelector(state),
    rapidfireType: state.challenge.rapidfireType,
    numWinning: parseInt(numWinning, 10),
    numDraws: numDraws ? parseInt(numDraws, 10) : undefined,
    prize: state.entry.myEntry.prize,
    sportId: state.challenge.sportId,
    winningAlgorithm: get('winning_algorithm', payStructure) || 'higher_score'
  };
}

const actions = {
  setPollDuration: challengeActions.setPollDuration,
  stopPoll: challengeActions.stopPollingChallenge,
  startPoll: challengeActions.pollChallenge
};

export default connect(mapStateToProps, actions)(RapidFireLiveChallenge);
